import { axiosInstance } from "../axios";
import { CAKey, RequestResponse, ResourceFetchParams, Scheme, SchemeInfo } from "../types";

export const getCAKeys: (
  params: ResourceFetchParams,
  deprecated?: boolean
) => Promise<RequestResponse<{ keys: CAKey[]; count: number }>> = async ({
  pageNumber,
  pageSize
}, deprecated) => {
  try {
    const deprecatedParam = deprecated !== undefined ? `&deprecated=${deprecated}` : "";
    const response = await axiosInstance.get(
      `/ca-key/get-all-keys?page=${pageNumber}&size=${pageSize}${deprecatedParam}`
    );
    return {
      error: false,
      data: {
        keys: response.data?.content || [],
        count: response.data?.count || 0
      }
    };
  } catch (err) {
    console.error("Unable to get CA keys: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const createCAKey: (payload: {
  scheme: Scheme;
  file: File;
  modulusLength: string;
}) => Promise<RequestResponse> = async ({ scheme, file, modulusLength }) => {
  try {
    const formData = new FormData();
    formData.append("ca-key-file", file);
    formData.append("modulusLength", `${Number(modulusLength) / 8}`);
    await axiosInstance.post(`/ca-key/create?scheme=${scheme}`, formData, {
      headers: {
        "content-type": "multipart/form-data"
      }
    });
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to create CA key: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const deprecateCAKey: (key: CAKey) => Promise<RequestResponse> = async (
  key
) => {
  try {
    await axiosInstance.patch(`/ca-key/deprecate-key/${key.id}`);
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to deprecate CA key: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const undeprecateCAKey: (
  key: CAKey
) => Promise<RequestResponse> = async (key) => {
  try {
    await axiosInstance.patch(`/ca-key/undeprecate-key/${key.id}`);
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to undeprecate CA key: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const activateCAKey: (
  key: CAKey
) => Promise<RequestResponse> = async (key) => {
  try {
    await axiosInstance.patch(`/ca-key/activate-key/${key.id}`);
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to activate CA key: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const deactivateCAKey: (
  key: CAKey
) => Promise<RequestResponse> = async (key) => {
  try {
    await axiosInstance.patch(`/ca-key/deactivate-key/${key.id}`);
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to deactivate CA key: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const createScheme: (payload: {
  code: string;
  name: string;
  description?: string;
}) => Promise<RequestResponse> = async ({ code, name, description }) => {
  try {
    await axiosInstance.post("/schemes/create", { code, name, description });
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to create scheme: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const updateScheme: (payload: {
  id: number;
  code: string;
  name: string;
  description?: string;
}) => Promise<RequestResponse> = async ({ id, code, name, description }) => {
  try {
    await axiosInstance.put(`/schemes/update/${id}`, { code, name, description });
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to update scheme: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const getAllSchemes: () => Promise<
  RequestResponse<SchemeInfo[]>
> = async () => {
  try {
    const response = await axiosInstance.get("/schemes/get-all-schemes");
    const schemes = (response.data?.data || []).filter(
      (scheme: SchemeInfo) => scheme.code && scheme.code.trim() !== "" && scheme.code !== "."
    );
    return {
      error: false,
      data: schemes
    };
  } catch (err) {
    console.error("Unable to get schemes: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const getCAKeysByScheme: (
  schemeCode: string,
  deprecated?: boolean
) => Promise<RequestResponse<CAKey[]>> = async (schemeCode, deprecated) => {
  try {
    const deprecatedParam =
      deprecated !== undefined ? `?deprecated=${deprecated}` : "";
    const response = await axiosInstance.get(
      `/ca-key/get-key-by-scheme/${schemeCode}${deprecatedParam}`
    );
    const keys: CAKey[] = response.data?.data || [];
    return {
      error: false,
      data: keys
    };
  } catch (err) {
    console.error("Unable to get CA keys by scheme: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};
